import { useEffect, useMemo, useState, useRef } from 'react';
import ReactGA from 'react-ga4';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faGear,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { Dropdown } from 'react-bootstrap';

import {
  WidgetMenuEntry,
  NewsWidget,
  LocalWidgetRouter,
  tabsWidgetRegistry,
  STALE_WIDGET_IDS,
  HasWidgetPermissions,
  DefaultWidgetContainerContext,
  WidgetHostContext,
} from '@/core/widgets/export';
import { useClickOutside } from '@/shared/hooks/export';
import { preferencesApi, useGlobalHeadlineView } from '@/core/preferences/export';
import { useEffectivePermissions } from '@/core/permissions/export';
import { Search, SearchResults, SearchProvider } from '@/core/search/export';
import { getBrandLogo, isAlphaFlashHost } from '@/shared/theme/export';
import { useOpenWidget, WidgetNavigationProvider } from '@/core/widget-navigation/export';
import { HelpButton } from '@/shared/components/export';
import DashboardNotifications from '@/app/dashboard/components/dashboard-notifications';

export default function TabWidgetHost({
  notificationSoundOn,
  setNotificationSoundOn,
}: {
  notificationSoundOn: boolean;
  setNotificationSoundOn: (on: boolean) => void;
}) {
  const permissions = useEffectivePermissions();
  const openWidget = useOpenWidget();

  const getDefaultWidget = () => {
    const widgetId = localStorage.getItem('tab-widget');
    if (widgetId) {
      if (STALE_WIDGET_IDS.includes(widgetId)) {
        localStorage.removeItem('tab-widget');
      } else {
        const savedWidget = tabsWidgetRegistry.getById(widgetId);
        if (savedWidget) return savedWidget;
      }
    }

    return tabsWidgetRegistry.getAll()[0];
  };

  const [allowedWidgets, setAllowedWidgets] = useState<NewsWidget[]>([]);
  const [selectedWidget, setSelectedWidget] = useState<NewsWidget>(getDefaultWidget());
  const [showScrollLeft, setShowScrollLeft] = useState(false);
  const [showScrollRight, setShowScrollRight] = useState(false);
  const [openMenus, setOpenMenus] = useState<string[]>([]);
  const [dropdownPositions, setDropdownPositions] = useState<
    Record<string, { top: number; left: number }>
  >({});
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { headlineView, setHeadlineView } = useGlobalHeadlineView();
  const toggleMenu = (key: string, parentKey = '', buttonRef?: HTMLButtonElement) => {
    if (buttonRef) {
      const rect = buttonRef.getBoundingClientRect();
      setDropdownPositions(prev => ({
        ...prev,
        [key]: {
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
        },
      }));
    }

    if (parentKey) {
      setOpenMenus(prev => {
        if (prev.includes(key)) {
          return prev.filter(k => k !== key);
        }
        const newMenus = prev.filter(k => !k.startsWith(`${parentKey}-`));
        return [...newMenus, key];
      });
    } else {
      setOpenMenus(prev => {
        if (prev.includes(key)) {
          return prev.filter(k => k !== key);
        } else {
          return [key];
        }
      });
    }
  };

  const isMenuOpen = (key: string) => openMenus.includes(key);

  const closeAllMenus = () => {
    setOpenMenus([]);
  };

  useClickOutside(dropdownRef, closeAllMenus);

  const renderTab = (widget: NewsWidget) => {
    const isActive = selectedWidget?.id === widget?.id;
    return (
      <div
        className={`d-flex align-items-center px-2 ${isActive ? 'border-bottom border-2 border-secondary' : ''}`}
        key={widget?.id + 'tab'}
      >
        <button
          onClick={() => setSelectedWidget(widget)}
          className={`bg-transparent border-0 pb-1 px-0 m-0 fw-bold position-relative  ${isActive ? 'text-secondary' : 'text-text-dark'}`}
          role="tab"
          aria-selected={isActive}
          aria-controls={widget?.id}
          tabIndex={isActive ? 0 : -1}
        >
          {widget.defaultTitle}
        </button>
      </div>
    );
  };

  const checkOverflow = () => {
    const el = tabsContainerRef.current;
    if (!el) return;
    setShowScrollLeft(el.scrollLeft > 0);
    setShowScrollRight(
      el.scrollWidth > el.clientWidth && el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    );
  };

  const scrollTabs = (dir: 'left' | 'right') => {
    const el = tabsContainerRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.2;
    el.scrollBy({ left: dir === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  };

  const handleDynamicWidgetSelect = (url: string) => {
    try {
      const widget = tabsWidgetRegistry.getById(url);
      if (widget) {
        setSelectedWidget(widget);
      }
    } catch (error) {
      console.warn(`Failed to create widget instance for ${url}:`, error);
    }
  };

  function getDropdownButton(
    name: string | JSX.Element,
    items: WidgetMenuEntry[],
    key: string,
    parentKey = '',
    level = 0,
  ) {
    const menuKey = `fact-${key}`;
    const dropdownPosition = dropdownPositions[menuKey] || { top: 0, left: 0 };

    const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
      toggleMenu(menuKey, parentKey, event.currentTarget);
    };

    return (
      <div
        key={menuKey}
        className="d-flex align-items-center px-2 position-relative font-nunito-sans"
      >
        <button
          onClick={handleToggle}
          className="bg-transparent border-0 pb-1 px-0 m-0 fw-bold position-relative text-text-dark"
          style={{ cursor: 'pointer' }}
        >
          {name}
          <span className="ms-2 d-inline-block" style={{ width: '12px', textAlign: 'center' }}>
            {isMenuOpen(menuKey) ? (
              <FontAwesomeIcon icon={faChevronDown} />
            ) : (
              <FontAwesomeIcon icon={faChevronRight} />
            )}
          </span>
        </button>
        {isMenuOpen(menuKey) && (
          <div
            ref={dropdownRef}
            className="position-fixed bg-base-light py-2 border rounded shadow-sm"
            style={{
              zIndex: 1000,
              minWidth: '200px',
              paddingLeft: '15px',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            {items.map((item, windex) => {
              if (item.url !== undefined) {
                return (
                  <Dropdown.Item
                    key={item.url + '-d-widget'}
                    onClick={() => handleDynamicWidgetSelect(item.url || '')}
                    className="px-2"
                  >
                    {item.name}
                  </Dropdown.Item>
                );
              } else {
                return getDropdownButton(
                  item.name,
                  item.children,
                  `${key}-${windex}`,
                  menuKey,
                  level + 1,
                );
              }
            })}
          </div>
        )}
      </div>
    );
  }

  useEffect(() => {
    const getAllowedWidgets = async () => {
      const widgets = tabsWidgetRegistry.getAll();
      const results = await Promise.all(
        widgets.map(async value => ({
          value,
          allowed: value.hasPermission && (await value.hasPermission(permissions)),
        })),
      );
      setAllowedWidgets(
        results
          .filter(r => r.allowed)
          .map(r => r.value)
          .filter(value => value?.id !== '/settings' && value?.id !== '/search'),
      );
    };
    getAllowedWidgets();
    // eslint-disable-next-line
  }, []);

  const regularWidgets = allowedWidgets.filter(value => value.id !== 'clock');
  const clockWidget = allowedWidgets.find(value => value.id === 'clock');

  useEffect(() => {
    checkOverflow();
    const handleResize = () => checkOverflow();
    window.addEventListener('resize', handleResize);
    const el = tabsContainerRef.current;
    if (el) el.addEventListener('scroll', checkOverflow);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (el) el.removeEventListener('scroll', checkOverflow);
    };
  }, [allowedWidgets]);

  useEffect(() => {
    localStorage.setItem('tab-widget', selectedWidget?.id);
  }, [selectedWidget]);

  useEffect(() => {
    if (selectedWidget?.id) {
      ReactGA.send({
        hitType: 'pageview',
        page: `/tab/~${selectedWidget?.id}`,
        title: selectedWidget.defaultTitle,
      });
    }
  }, [selectedWidget]);

  const latestRef = useRef({ allowedWidgets, selectedWidget });
  latestRef.current = { allowedWidgets, selectedWidget };

  const widgetHost = useMemo(
    () => ({
      get activeWidgetIds(): string[] {
        return latestRef.current.allowedWidgets.map(widget => widget.id);
      },
      removeWidgetById(id: string) {
        console.log('Widget removal not supported in tabs');
      },
      addWidgetById(id: string) {
        console.log('Widget addition not supported in tabs');
      },
      get hostId(): string {
        return 'tabs';
      },
      get currentViewId(): string {
        return latestRef.current.selectedWidget?.id || tabsWidgetRegistry.getAll()[0].id;
      },
      async setWidgetPath(id: string, path: string) {
        console.log('Widget path setting not supported in tabs');
      },
      registerWidgetNavigator(id: string, navigate: (path: string) => void) {
        return () => {};
      },
      registerActiveWidgetSelector(select: (id: string, path?: string) => void) {
        return () => {};
      },
      async getWidgetPreferences<T>(id: string): Promise<T> {
        return ((await preferencesApi.getPreferences(`widget-${id}`)) as T) || ({} as T);
      },
      async saveWidgetPreferences<T>(id: string, t: T): Promise<T> {
        await preferencesApi.upsertPreferences(`widget-${id}`, t);
        return t;
      },
    }),

    [],
  );

  return (
    <WidgetHostContext.Provider value={widgetHost}>
      <WidgetNavigationProvider>
        <SearchProvider filterType={isAlphaFlashHost() ? 'event' : 'bullet'}>
          <div className={'d-flex h-100 flex-column p-1'}>
            <div>
              <div className={'d-flex justify-content-between m-1'}>
                {/*  eslint-disable-next-line */}
                <a className="navbar-brand" href="#">
                  <img alt="logo" src={getBrandLogo()} height={'24'} />
                </a>
                <div className={'d-flex'}>
                  <div className={'d-flex align-items-center'}>
                    <div className="form-check form-switch mx-2 mb-0 d-flex align-items-center">
                      <input
                        className="form-check-input border-secondary me-2"
                        type="checkbox"
                        id="headlineView"
                        checked={headlineView}
                        onChange={() => setHeadlineView(!headlineView)}
                      />
                      <label className="form-check-label mb-0 text-nowrap" htmlFor="headlineView">
                        Headline View
                      </label>
                    </div>
                    <Search />
                    <DashboardNotifications
                      notificationSoundOn={notificationSoundOn}
                      setNotificationSoundOn={setNotificationSoundOn}
                    />
                    <button
                      style={{ fontFamily: 'Inter' }}
                      className={'btn btn-sm ms-2 w-100 btn-outline-secondary'}
                      onClick={() => openWidget('settings')}
                    >
                      <FontAwesomeIcon icon={faGear} />
                    </button>
                    <HelpButton
                      style={{ fontFamily: 'Inter' }}
                      className={'btn btn-sm ms-2 w-100 btn-outline-secondary'}
                    />
                  </div>
                </div>
              </div>

              <div className="my-3 position-relative border-bottom border-text-light pb-3">
                <div className="position-absolute end-0 z-1 px-2">
                  <button
                    className="btn btn-link btn-sm"
                    onClick={() => scrollTabs('left')}
                    aria-label="Scroll left"
                    disabled={!showScrollLeft}
                  >
                    <FontAwesomeIcon
                      style={{ visibility: showScrollLeft ? 'visible' : 'hidden' }}
                      icon={faChevronLeft}
                    />
                  </button>

                  <button
                    className="btn btn-link btn-sm"
                    onClick={() => scrollTabs('right')}
                    aria-label="Scroll right"
                    disabled={!showScrollRight}
                  >
                    <FontAwesomeIcon
                      style={{ visibility: showScrollRight ? 'visible' : 'hidden' }}
                      icon={faChevronRight}
                    />
                  </button>
                </div>
                <div
                  className="d-flex align-items-end overflow-auto scrollbar-hide"
                  ref={tabsContainerRef}
                  style={{ scrollBehavior: 'smooth', whiteSpace: 'nowrap', marginRight: '5rem' }}
                  tabIndex={0}
                  role="tablist"
                  aria-label="Tabs"
                >
                  {regularWidgets.map(renderTab)}

                  {tabsWidgetRegistry
                    .getFactories()
                    .filter(value => value.getListableUrls().length > 0)
                    .map((value, index) => {
                      return getDropdownButton(
                        value.headerPanel,
                        value.getListableUrls(),
                        `${index}`,
                      );
                    })}

                  {clockWidget && renderTab(clockWidget)}
                </div>
              </div>
            </div>

            <SearchResults />

            <div className={'d-flex overflow-auto h-100 vw-100'}>
              <div className={`h-100 overflow-auto flex-grow-1 flex-shrink-1 mx-2`}>
                {selectedWidget && (
                  <DefaultWidgetContainerContext widget={selectedWidget}>
                    <HasWidgetPermissions widget={selectedWidget}>
                      <LocalWidgetRouter
                        widget={selectedWidget}
                        key={selectedWidget?.id}
                        onNavigate={s => {}}
                      />
                    </HasWidgetPermissions>
                  </DefaultWidgetContainerContext>
                )}
              </div>
            </div>
          </div>
        </SearchProvider>
      </WidgetNavigationProvider>
    </WidgetHostContext.Provider>
  );
}
